const SUBIFY_WIDGET_SELECTOR = '.subify-root';

function removeSubifyWidgets(root) {
  root.querySelectorAll(SUBIFY_WIDGET_SELECTOR).forEach((widget) => widget.remove());
}

// Subi's own storefront widget renders itself asynchronously (timing varies),
// so a MutationObserver removes it whenever it actually appears instead of
// guessing a fixed delay. When it mounts, Subi also swaps the form's
// selling_plan input for an empty one of its own, so every box syncs again
// afterwards — otherwise a preselected subscription goes to the cart as a
// one-time purchase.
const subifyWidgetObserver = new MutationObserver((mutations) => {
  let subifyTouched = false;
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches(SUBIFY_WIDGET_SELECTOR)) {
        node.remove();
        subifyTouched = true;
      } else if (node.matches('input[name="selling_plan"]') || node.querySelector(SUBIFY_WIDGET_SELECTOR)) {
        removeSubifyWidgets(node);
        subifyTouched = true;
      }
    });
  });
  if (subifyTouched) {
    document.querySelectorAll('selling-plan-box').forEach((box) => box.syncState?.());
  }
});
subifyWidgetObserver.observe(document.body, { childList: true, subtree: true });
removeSubifyWidgets(document);

if (!customElements.get('selling-plan-box')) {
  class SellingPlanBox extends HTMLElement {
    constructor() {
      super();
      this.onOptionChange = this.handleOptionChange.bind(this);
      this.onQuantityChange = this.handleQuantityChange.bind(this);
      this.onSubscriptionMinus = this.handleSubscriptionMinus.bind(this);
      this.onSubscriptionPlus = this.handleSubscriptionPlus.bind(this);
      this.onFormData = this.handleFormData.bind(this);
      this.onCardClick = this.handleCardClick.bind(this);
    }

    connectedCallback() {
      this.oneTimeRadio = this.querySelector('[data-radio-one-time]');
      this.subscriptionRadio = this.querySelector('[data-radio-subscription]');
      this.frequencySelect = this.querySelector('[data-frequency]');
      this.submitPrice = this.querySelector('[data-submit-price]');
      this.badgePercent = this.querySelector('[data-badge-percent]');
      this.priceOneTime = this.querySelector('[data-one-time-price]');
      this.compareOneTime = this.querySelector('[data-one-time-compare-price]');
      this.oneTimePrice = Number(this.dataset.oneTimePrice) || 0;
      this.oneTimeCompareAtPrice = Number(this.dataset.oneTimeCompareAtPrice) || 0;
      this.moneyFormat = this.dataset.moneyFormat;

      this.primaryQuantity = this.querySelector('[data-selling-plan-box-primary-quantity]');
      this.subscriptionQuantity = this.querySelector('[data-selling-plan-box-subscription-quantity-input]');
      this.subscriptionMinus = this.querySelector('[data-selling-plan-box-subscription-minus]');
      this.subscriptionPlus = this.querySelector('[data-selling-plan-box-subscription-plus]');
      this.comparePriceEl = this.querySelector('[data-selling-plan-compare-price]');
      this.priceEl = this.querySelector('[data-selling-plan-price]');

      this.oneTimeRadio?.addEventListener('change', this.onOptionChange);
      this.subscriptionRadio?.addEventListener('change', this.onOptionChange);
      this.frequencySelect?.addEventListener('change', this.onOptionChange);
      this.primaryQuantity?.addEventListener('change', this.onQuantityChange);
      this.subscriptionQuantity?.addEventListener('change', this.onQuantityChange);
      this.subscriptionMinus?.addEventListener('click', this.onSubscriptionMinus);
      this.subscriptionPlus?.addEventListener('click', this.onSubscriptionPlus);
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.form?.addEventListener('formdata', this.onFormData);
      this.cards = this.querySelectorAll('.selling-plan-box__option');
      this.cards.forEach((card) => card.addEventListener('click', this.onCardClick));

      this.syncState();
    }

    disconnectedCallback() {
      this.oneTimeRadio?.removeEventListener('change', this.onOptionChange);
      this.subscriptionRadio?.removeEventListener('change', this.onOptionChange);
      this.frequencySelect?.removeEventListener('change', this.onOptionChange);
      this.primaryQuantity?.removeEventListener('change', this.onQuantityChange);
      this.subscriptionQuantity?.removeEventListener('change', this.onQuantityChange);
      this.subscriptionMinus?.removeEventListener('click', this.onSubscriptionMinus);
      this.subscriptionPlus?.removeEventListener('click', this.onSubscriptionPlus);
      this.form?.removeEventListener('formdata', this.onFormData);
      this.cards?.forEach((card) => card.removeEventListener('click', this.onCardClick));
    }

    // A click anywhere on a card (promo text, price, quantity buttons, padding)
    // selects its option, so the card doesn't only react to the radio + label.
    // A click on the radio itself has already checked it by now.
    handleCardClick(event) {
      const radio = event.currentTarget.querySelector('.selling-plan-box__radio');
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    getSellingPlan() {
      if (!this.subscriptionRadio?.checked) return '';
      return this.frequencySelect?.selectedOptions[0]?.value || '';
    }

    // Subi swaps the hidden selling_plan input for one inside its own widget,
    // which the observer above then removes along with the widget, so the plan
    // is written straight into the submitted data instead of relying on the DOM.
    // consultation-upsell.js may already have moved the product into items[].
    handleFormData(event) {
      const data = event.formData;
      if (!data.has('id')) return;
      const sellingPlan = this.getSellingPlan();
      if (sellingPlan) data.set('selling_plan', sellingPlan);
      else data.delete('selling_plan');
    }

    handleOptionChange(event) {
      if (event.target === this.frequencySelect && this.subscriptionRadio) {
        this.subscriptionRadio.checked = true;
      }
      this.syncState();
    }

    handleQuantityChange() {
      this.syncState();
    }

    getOneTimeQuantity() {
      return Number.parseFloat(this.primaryQuantity?.value) || 1;
    }

    getSubscriptionQuantity() {
      return Number.parseFloat(this.subscriptionQuantity?.value) || 1;
    }

    // The quantity of the option that is currently selected — the only one that
    // ends up in the cart.
    getQuantity() {
      return this.subscriptionRadio?.checked ? this.getSubscriptionQuantity() : this.getOneTimeQuantity();
    }

    handleSubscriptionMinus() {
      this.stepSubscriptionQuantity(-1);
    }

    handleSubscriptionPlus() {
      this.stepSubscriptionQuantity(1);
    }

    // The subscription card has its own quantity input, unrelated to the
    // one-time-purchase row. Its buttons are not Dawn's quantity-input buttons,
    // so the stepping is done here.
    stepSubscriptionQuantity(direction) {
      const input = this.subscriptionQuantity;
      if (!input) return;
      const step = Number.parseFloat(input.step) || 1;
      const min = Number.parseFloat(input.min) || 0;
      const max = input.max ? Number.parseFloat(input.max) : Infinity;
      const current = Number.parseFloat(input.value) || min;
      const next = Math.min(Math.max(current + direction * step, min), max);
      if (next === current) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Both quantity inputs live in the same product form, so only the selected
    // option's input may carry name="quantity" — otherwise two values would be
    // posted to /cart/add.
    syncSubmittedQuantity(isSubscription) {
      if (!this.subscriptionQuantity) return;
      if (isSubscription) {
        this.primaryQuantity?.removeAttribute('name');
        this.subscriptionQuantity.setAttribute('name', 'quantity');
      } else {
        this.subscriptionQuantity.removeAttribute('name');
        this.primaryQuantity?.setAttribute('name', 'quantity');
      }
    }

    syncState() {
      const isSubscription = Boolean(this.subscriptionRadio?.checked);
      const selectedOption = this.frequencySelect?.selectedOptions[0];
      const sellingPlanValue = isSubscription && selectedOption ? selectedOption.value : '';
      const oneTimeQuantity = this.getOneTimeQuantity();
      const subscriptionQuantity = this.getSubscriptionQuantity();
      const quantity = isSubscription ? subscriptionQuantity : oneTimeQuantity;
      const selectedPrice = selectedOption ? Number(selectedOption.dataset.price) : null;
      const selectedComparePrice = selectedOption ? Number(selectedOption.dataset.comparePrice) : null;

      this.syncSubmittedQuantity(isSubscription);

      // Sets every matching field, in case Subi's widget script injected its
      // own competing "selling_plan" hidden input into the same form.
      this.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
        input.value = sellingPlanValue;
      });

      const oneTimePriceFormatted = window.formatMoney(this.oneTimePrice * oneTimeQuantity, this.moneyFormat);

      if (this.priceOneTime) {
        this.priceOneTime.textContent = oneTimePriceFormatted;
      }

      if (this.compareOneTime && this.oneTimeCompareAtPrice > this.oneTimePrice) {
        this.compareOneTime.textContent = window.formatMoney(
          this.oneTimeCompareAtPrice * oneTimeQuantity,
          this.moneyFormat
        );
      }

      if (this.submitPrice) {
        this.submitPrice.textContent =
          isSubscription && selectedPrice
            ? window.formatMoney(selectedPrice * quantity, this.moneyFormat)
            : oneTimePriceFormatted;
      }

      if (this.badgePercent && selectedOption?.dataset.discountPercent) {
        this.badgePercent.textContent = `${selectedOption.dataset.discountPercent}%`;
      }

      if (this.priceEl && selectedPrice) {
        this.priceEl.textContent = window.formatMoney(selectedPrice * subscriptionQuantity, this.moneyFormat);
      }

      if (this.comparePriceEl && selectedComparePrice) {
        this.comparePriceEl.textContent = window.formatMoney(
          selectedComparePrice * subscriptionQuantity,
          this.moneyFormat
        );
      }
    }
  }

  customElements.define('selling-plan-box', SellingPlanBox);
}
